import { getAuth, getRedirectResult } from "firebase/auth"
import { initFirebaseApp, initializeGoogleLogin } from "lib/auth/firebase"
import React, { createContext, useContext, useEffect, useMemo, useState } from "react"

export type LoginState = "successful" | "error" | "pending" | "notStarted"
export type TokenProvider = () => Promise<string>

// Instead of using the Firebase User type we create our own since we only use
// these two attributes anyways. This makes our PlaywrightAuthProvider implementation
// easier.
type ROIUser = {
  getIdToken: () => Promise<string>
  uid: string
}

type AuthContextValue = {
  token: TokenProvider
  user: ROIUser | undefined
  loginState: LoginState
  signOut: () => Promise<void>
  signIn: () => Promise<void>
}

const emptyToken = (): Promise<never> => Promise.reject(new Error("no token available"))

export const Context = createContext<AuthContextValue | null>({
  loginState: "notStarted",
  token: emptyToken,
  user: undefined,
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  signIn: () => Promise.resolve(),
  signOut: () => Promise.resolve(),
})

interface AuthContextProps {
  children: React.ReactNode
}

const FirebaseAuthProvider = (): AuthContextValue => {
  const [user, setUser] = useState<ROIUser | undefined>()
  const [loginState, setLoginState] = useState<LoginState>("notStarted")
  initFirebaseApp()
  const auth = getAuth()

  useEffect(() => {
    return auth.onAuthStateChanged(async (u) => {
      if (u) {
        setUser(u)
        setLoginState("successful")
      } else {
        setUser(undefined)
        setLoginState("notStarted")
      }
    })
  }, [auth])

  const value = useMemo(() => {
    const login = async (): Promise<void> => {
      setLoginState("pending")
      try {
        const credentials = await getRedirectResult(auth)
        if (credentials) {
          setLoginState("successful")
          setUser(credentials.user)
        } else {
          await initializeGoogleLogin()
        }
      } catch {
        setLoginState("error")
      }
    }

    return {
      token: () => user?.getIdToken() ?? emptyToken(),
      user,
      loginState,
      signOut: () => auth.signOut(),
      signIn: login,
    }
  }, [user, loginState, auth])

  return value
}

const PlaywrightAuthProvider = (): AuthContextValue => {
  return {
    loginState: "successful",
    signIn: () => Promise.reject(new Error("not implemented")),
    signOut: () => Promise.reject(new Error("not implemented")),
    token: () => Promise.resolve("a perfectly valid, but always ignored, token"),
    user: { getIdToken: () => Promise.resolve("id"), uid: "uid" },
  }
}

export const AuthContext = ({ children }: AuthContextProps): React.JSX.Element => {
  const inPlaywright = import.meta.env.VITE_PLAYWRIGHT
  const value = inPlaywright ? PlaywrightAuthProvider() : FirebaseAuthProvider()

  return <Context.Provider value={value}>{children}</Context.Provider>
}

export function useAuth(): AuthContextValue {
  const auth = useContext(Context)

  if (!auth) {
    throw new Error("useAuth must be used within a AuthContext")
  }

  return auth
}
